import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Typography, Segmented } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { BiCheckCircle } from 'react-icons/bi';
import Card from '../card/Card';
import { Button } from '../ui';
import { DynamicForm } from '../form/DynamicForm';
import { useTheme } from '../../contexts/ThemeContext';
import SynchronizationService from '../../services/synchronizationService';
import NiboOAuthService from '../../services/niboOAuthService';
import { Toast } from '../ui';
import SelectNiboCompaniesModal from '../modal/NiboModal';
import Modal from '../modal/Modal';
import ContaAzulOAuthService from '../../services/ContaAzulOAuthService';
import BlingOAuthService from '../../services/BlingOAuthService';
import Api from '../../services/api';
import { ExcelAiPanel } from './excelAi';

const { Text } = Typography;

const MODO_PADRAO = 'padrao';
const MODO_IA = 'ia';

const SynchronizationApiConfig = ({
  erp,
  appKey,
  projectId,
  appSecret,
  type,
  nomeIntegracao,
  login,
  senha,
  arquivos,
  accessToken,
  ExternalId,
  authToken,
  idSync,
  onGarantirSalvo,
  onRunIaFinalizado,
  agrupadorSyncs = [],
  onChange,
  onNiboAuthorized,
  onTestConnection,
  testingConnection,
  isTestLocked,
  IsConfigured,
  onSaveBeforeAuthorize,
}) => {
  const { theme } = useTheme();
  const [projectSyncs, setProjectSyncs] = useState([]);
  const [loadingSyncs, setLoadingSyncs] = useState(false);
  const [niboCompanies, setNiboCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [niboOwner, setNiboOwner] = useState(null);
  // Estado para controlar loading do download
  const [downloadingModel, setDownloadingModel] = useState(false);
  // Loading do salvamento/autorização disparado pelos botões OAuth.
  const [savingBeforeAuth, setSavingBeforeAuth] = useState(false);
  const [blingAuthorizationPhase, setBlingAuthorizationPhase] = useState(null);
  const blingAuthorizationInFlight = useRef(false);

  // Modo de processamento do Excel. É escolha de VISTA, não configuração salva: os dois modos
  // convivem na mesma empresa e o que vale é o último processamento. Começa no padrão, que é o
  // comportamento que já existia — quem nunca ouviu falar da IA não é surpreendido.
  const [modoExcel, setModoExcel] = useState(MODO_PADRAO);

  const loadProjectSynchronizations = useCallback(async () => {
    try {
      setLoadingSyncs(true);
      
      // Buscar todas as sincronizações e filtrar pelo projeto
      const allSyncs = await SynchronizationService.getSynchronizationsSimplified({
        page: 1,
        pageSize: 2000
      });
      
      const rawSyncs = Array.isArray(allSyncs)
        ? allSyncs
        : Array.isArray(allSyncs?.data)
          ? allSyncs.data
          : Array.isArray(allSyncs?.response)
            ? allSyncs.response
            : [];

      const projectSyncs = rawSyncs.filter((sync) =>
        (sync.projectId === projectId || sync.project?.id === projectId) &&
        Number(sync.erp) === Number(erp) &&
        String(sync.id) !== String(idSync) &&
        String(sync.type ?? sync.Type ?? 1) === "1"
      );

      // Transformar para formato de opções do select
      const syncOptions = projectSyncs.map(sync => ({
        value: sync.id,
        label: `${sync.name || sync.nome} (${getErpName(sync.erp)})`
      }));

      const selectedFallbackOptions = (Array.isArray(agrupadorSyncs) ? agrupadorSyncs : [])
        .filter((id) => !syncOptions.some((opt) => String(opt.value) === String(id)))
        .map((id) => ({
          value: id,
          label: `Sincronização vinculada (${id})`,
        }));
      
      setProjectSyncs([...syncOptions, ...selectedFallbackOptions]);
    } catch (error) {
      console.error('Erro ao carregar sincronizações do projeto:', error);
    } finally {
      setLoadingSyncs(false);
    }
  }, [projectId, erp, idSync, agrupadorSyncs]);

  useEffect(() => {
    if (type === 2 && projectId && erp !== undefined && erp !== null) {
      loadProjectSynchronizations();
    }
  }, [projectId, type, erp, loadProjectSynchronizations]);

  // Helper para obter nome do ERP
  const getErpName = (erpId) => {
    const erpNames = {
      0: 'Omie',
      1: 'Conta Azul',
      2: 'Nibo',
      3: 'Excel',
      5: 'Conta Azul',
      //6: 'Bling',
      7: 'Vehro'
    };
    return erpNames[erpId] || 'ERP';
  };

  // Configuração do formulário baseada no tipo de ERP
  const getFormConfig = () => {
    // Se for tipo 2 (agrupador), mostrar configuração especial
    if (type === 2) {
      return [
        {
          columns: 1,
          questions: [
            
            {
              id: 'agrupadorSyncs',
              label: 'Sincronizações do Projeto',
              type: 'multiselect',
              placeholder: 'Selecione as sincronizações para agrupar',
              options: projectSyncs,
              loading: loadingSyncs,
              disabled: !projectId,
              helpText: 'Selecione as sincronizações que serão agrupadas nesta integração',
              showSearch: {
                filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            }
          ]
        }
      ];
    }

    // Configurações normais para outros tipos
    switch (erp) {
      case 0: // Omie
        return [
          {
            columns: 3,
            questions: [
             
              {
                id: 'appKey',
                label: 'APP Key',
                type: 'text',
                placeholder: 'Digite seu APP Key',
                autoComplete: 'off',
                name: 'app_key_' + Math.random().toString(36).substr(2, 9)
              },
              {
                id: 'appSecret',
                label: 'APP Secret',
                type: 'text',
                placeholder: 'Digite seu APP Secret',
                autoComplete: 'off',
                name: 'app_secret_' + Math.random().toString(36).substr(2, 9)
              }
            ]
          }
        ];

      case 1: // ContaAzul
          return [
            {
              columns: 3,
              questions: [
              
                {
                  id: 'login',
                  label: 'Usuário',
                  type: 'text',
                  placeholder: 'Digite seu usuário',
                  autoComplete: 'off',
                  name: 'user_field_' + Math.random().toString(36).substr(2, 9)
                },
                {
                  id: 'senha',
                  label: 'Chave de Acesso',
                  type: 'password',
                  minLength: 3,
                  requireUppercase: false,
                  requireLowercase: false,
                  requireNumbers: false,
                  requireSpecialChars: false,
                  showStrengthIndicator: false,
                  placeholder: 'Digite sua chave de acesso',
                  autoComplete: 'new-password',
                  name: 'access_key_' + Math.random().toString(36).substr(2, 9)
                },
                {
                  id: 'authToken',
                  label: 'Token de Autenticação',
                  type: 'text',
                  placeholder: 'Digite seu Token de Autenticação',
                  autoComplete: 'off',
                  name: 'auth_token_' + Math.random().toString(36).substr(2, 9)
                }
              ]
            }
          ];

      case 3: // Excel
      case 6: // Bling — sem credenciais de texto (autenticação via OAuth)
        return [];

      case 7: // Vehro — um token da API por empresa; fica só no integrador
        return [
          {
            columns: 1,
            questions: [
              {
                id: 'authToken',
                label: 'Token da API Vehro',
                type: 'password',
                minLength: 3,
                requireUppercase: false,
                requireLowercase: false,
                requireNumbers: false,
                requireSpecialChars: false,
                showStrengthIndicator: false,
                placeholder: idSync ? 'Deixe em branco para manter o token atual' : 'Cole o token da API Vehro',
                autoComplete: 'new-password',
                name: 'vehro_token_' + Math.random().toString(36).substr(2, 9)
              }
            ]
          }
        ];

      default:
        return [];
    }
  };

  const autorizarNibo = async () => {
    try {
      const sessionId = idSync || crypto.randomUUID();
      const authResult = await NiboOAuthService.initiateAuth(sessionId);

      if (authResult.organizations && authResult.organizations.length > 0) {
        setNiboCompanies(authResult.organizations);
        setSelectedCompany(null);
        setNiboOwner(authResult.owner);
        setShowCompaniesModal(true);
      } else {
        Toast.warning('Nenhuma empresa encontrada na conta Nibo.');
      }
    } catch (error) {
      console.error('Erro na autenticação OAuth2:', error);
      Toast.error(error?.message || 'Erro na autenticação com Nibo. Tente novamente.');
    }
  };

  const executarAutorizacaoContaAzul = async (syncId = idSync) => {
    try {
      const authResult = await ContaAzulOAuthService.completeAuth(syncId);
      if (authResult) {
        onChange({
          IsConfigured: true
        });
        Toast.success('Autenticação com Conta Azul realizada com sucesso!');
      }
      
    } catch (error) {
      console.error('Erro na autenticação OAuth2:', error);
      Toast.error('Erro na autenticação com Conta Azul. Tente novamente.');
    }
  };

  const executarAutorizacaoBling = async (syncId = idSync, popup = null) => {
    try {
      setBlingAuthorizationPhase('authorizing');
      const authResult = await BlingOAuthService.completeAuth(syncId, popup);
      if (authResult) {
        // Mark configured immediately so UI updates without waiting for reload
        onChange({ IsConfigured: true });
        Toast.success('Autenticação com Bling realizada com sucesso!');

        // Best-effort reload to reflect actual backend state (e.g. Sincronizando status)
        try {
          const reloaded = await SynchronizationService.getSynchronizationById(syncId);
          const data = reloaded?.response || reloaded?.Response || reloaded;
          if (data) onChange(data);
        } catch (_) {
          // Reload is best-effort — isConfigured=true already set above
        }
      }
    } catch (error) {
      console.error('Erro na autenticação OAuth2 Bling:', error);
      Toast.error(error?.message || 'Erro na autenticação com Bling. Tente novamente.');
    }
  };

  const iniciarAutorizacaoBling = async () => {
    // State updates are asynchronous; the ref makes the double-click guard immediate.
    if (blingAuthorizationInFlight.current) return;

    blingAuthorizationInFlight.current = true;
    setSavingBeforeAuth(true);
    setBlingAuthorizationPhase('saving');
    let popup = null;

    try {
      // Keep the browser user gesture alive without starting OAuth yet. The blank
      // popup is closed if validation or persistence fails.
      popup = BlingOAuthService.openPopup('pending');
      const syncIdAutorizacao = typeof onSaveBeforeAuthorize === 'function'
        ? await onSaveBeforeAuthorize()
        : idSync;

      // The save routine only returns an id after the backend has persisted the
      // Sync. Without it, OAuth must never be launched.
      if (!syncIdAutorizacao) {
        try { popup.close(); } catch (_) { /* ignore */ }
        return;
      }

      await executarAutorizacaoBling(syncIdAutorizacao, popup);
    } catch (error) {
      try { popup?.close(); } catch (_) { /* ignore */ }
      console.error('Erro ao preparar autenticação OAuth2 Bling:', error);
      Toast.error(error?.message || 'Erro ao preparar a autenticação com Bling. Tente novamente.');
    } finally {
      setBlingAuthorizationPhase(null);
      setSavingBeforeAuth(false);
      blingAuthorizationInFlight.current = false;
    }
  };

  const autorizarBling = () => {
    if (IsConfigured) {
      Modal.confirm({
        title: 'Reautenticar Bling',
        content: 'Você está prestes a reautenticar a sincronização com o Bling. Tem certeza que deseja prosseguir?',
        okText: 'Reautorizar',
        cancelText: 'Cancelar',
        onOk: iniciarAutorizacaoBling
      });
    } else {
      iniciarAutorizacaoBling();
    }
  };

  const autorizarContaAzul = async () => {
    // Fluxo unificado: valida e salva usando exatamente a mesma lógica do botão
    // "Salvar" antes de iniciar a autorização com o Conta Azul.
    let syncIdAutorizacao = idSync;

    if (typeof onSaveBeforeAuthorize === 'function') {
      try {
        setSavingBeforeAuth(true);
        syncIdAutorizacao = await onSaveBeforeAuthorize();
      } finally {
        setSavingBeforeAuth(false);
      }

      // Validação/salvamento falhou: os erros já foram exibidos pelo salvamento
      // e a autenticação não deve prosseguir.
      if (!syncIdAutorizacao) return;
    }

    if (IsConfigured) {
      Modal.confirm({
        title: 'Reautenticar Sincronização',
        content: 'Você está prestes a reautenticar a sincronização com o Conta Azul. Tem certeza que deseja prosseguir?',
        okText: 'Reautorizar',
        cancelText: 'Cancelar',
        onOk: () => {
          executarAutorizacaoContaAzul(syncIdAutorizacao);
        }
      });
    } else {
      executarAutorizacaoContaAzul(syncIdAutorizacao);
    }
  };

  const downloadExcelArquivoAtual = async () => {
    if (!arquivos) return;
    try {
      setDownloadingModel(true);
      const response = await Api.get(`/sync/getFile/${encodeURIComponent(arquivos)}`);
      const url = response.data?.url;
      if (!url) {
        Toast.error('URL de download não encontrada');
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = arquivos;
      document.body.appendChild(a);
      a.click();
      a.remove();
      Toast.success('Arquivo baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar arquivo Excel da sincronização:', error);
      Toast.error('Erro ao baixar arquivo atual da sincronização');
    } finally {
      setDownloadingModel(false);
    }
  };

  // Verificar se o botão de teste deve estar habilitado
  const isTestEnabled = () => {
    // Se tem idSync (sincronização existente), permitir teste mesmo com credentials vazios
    const isExistingSync = idSync && idSync !== null;
    
    if (type === 2) {
      return nomeIntegracao && agrupadorSyncs && agrupadorSyncs.length > 0;
    }

    switch (erp) {
      case 0: // Omie
        return isExistingSync || (appKey && appSecret);
      case 1: // ContaAzul
        return isExistingSync || (nomeIntegracao && login && senha && authToken);
      case 2: // Nibo
        return isExistingSync || (nomeIntegracao && login && senha);
      case 3: // Excel
        return false; // Excel não precisa testar conexão
      default:
        return false;
    }
  };

  // Obter nome do ERP para exibição
  const getErpDisplayName = () => {
    if (type === 2) {
      return 'Agrupador de Sincronizações';
    }
    
    const erpNames = {
      0: 'Omie',
      1: 'Conta Azul',
      2: 'Nibo',
      3: 'Excel',
      5: 'Conta Azul',
      6: 'Bling',
      7: 'Vehro'
    };
    return erpNames[erp] || 'ERP';
  };

  const formConfig = getFormConfig();
  
  

 
  // Criar formValues baseado no tipo
  let formValues = {};
  if (type === 2) {
    formValues = { 
      agrupadorSyncs: agrupadorSyncs || []
    };
  } else if (erp === 0) {
    formValues = { 
      appKey: appKey || '', 
      appSecret: appSecret || '' 
    };
  } else if (erp === 3) {
    formValues = { 
      login: login || '', 
      senha: senha || '' 
    };
  } else if (erp === 4) {
    formValues = { 
      accessToken: accessToken || ''
    };
  } else if (erp === 7) {
    formValues = {
      authToken: authToken || ''
    };
  }
  
  else {
    formValues = { 
      login: login || '', 
      senha: senha || '' 
    };
  }

  return (
    <>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row'}}>
            <LockOutlined style={{ fontSize: 16, color: theme.primary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {type === 2
                ? 'Configuração do Agrupador'
                : erp === 3
                  ? 'Processamento das planilhas'
                  : 'Credenciais de Autenticação'}
            </span>
            {erp === 3 && modoExcel === MODO_PADRAO && (
              <Button 
                type="default" 
                style={{ padding: '0 12px', height: 32, backgroundColor: theme.primary, color: theme.white }}
                loading={downloadingModel}
                disabled={downloadingModel}
                onClick={async () => {
                  try {
                    setDownloadingModel(true);
                    const response = await SynchronizationService.downloadModeloPadrao();
                    const url = window.URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'ModeloXLSX.xlsx';
                    a.click();
                    window.URL.revokeObjectURL(url);
                    Toast.success('Modelo baixado com sucesso!');
                  } catch (error) {
                    console.error('Erro ao baixar modelo:', error);
                    Toast.error('Erro ao baixar o modelo padrão');
                  } finally {
                    setDownloadingModel(false);
                  }
                }}
              >
                Baixar Modelo
              </Button>
            )}
            {/* Validação para tipo 2 sem projectId */}
        {type === 2 && !projectId && (
          <div style={{
            borderRadius: 6,
          }}>
            <Text style={{ 
              color: '#cf1322',
              fontSize: 14,
              fontWeight: 500
            }}>
              * Selecione um projeto primeiro
            </Text>
          
          </div>
        )}
          </div>
        }
        style={{ marginBottom: 24 }}
        extra={
          erp === 0 && type !== 2 && ( // Não mostrar botão de teste para Excel ou Agrupador
            <Button
              type="primary"
              icon={<BiCheckCircle style={{ color: theme.white, fontSize: 20 }} />}
              onClick={onTestConnection}    
              loading={testingConnection}
              disabled={!isTestEnabled() || testingConnection || isTestLocked}
              style={{
                color: theme.white,
                backgroundColor: theme.success,
                borderColor: theme.success
              }}
            >
              Testar Conexão
            </Button>
          )
        }
        data-tour-id="sync-credentials"
      >
        <Text style={{ 
          display: 'block',
          marginBottom: 16,
          color: '#8c8c8c',
          fontSize: 14
        }}>
          {type === 2
            ? 'Configure o agrupador de sincronizações para o projeto selecionado'
            : erp === 3
              ? 'Escolha como as planilhas desta empresa serão transformadas em dados.'
              : `Configure suas credenciais do ${getErpDisplayName()}`
          }
        </Text>

        {erp === 3 && (
          <div style={{ marginBottom: 20 }}>
            <Segmented
              value={modoExcel}
              onChange={setModoExcel}
              options={[
                { label: 'Modelo padrão', value: MODO_PADRAO },
                { label: 'Processar com IA', value: MODO_IA },
              ]}
              block
            />
            <Text style={{ display: 'block', marginTop: 8, color: '#8c8c8c', fontSize: 13 }}>
              {modoExcel === MODO_PADRAO
                ? 'Uma planilha no modelo da D4C. Baixe o modelo, preencha e anexe.'
                : 'Várias planilhas no formato que você já tem. A IA identifica o conteúdo e normaliza.'}
            </Text>
          </div>
        )}

        {erp === 3 && modoExcel === MODO_IA ? (
          <ExcelAiPanel
            syncId={idSync}
            onGarantirSalvo={onGarantirSalvo}
            onRunFinalizado={onRunIaFinalizado}
          />
        ) : erp === 3 ? (
          <>
            <DynamicForm
              formConfig={[
                {
                  columns: 2,
                  questions: [
                    
                    {
                      id: 'arquivos',
                      label: 'Arquivo:',
                      type: 'excelUpload',
                      idSync: idSync,
                      width: '10%'
                    }
                  ]
                }
              ]}
              values={{ nomeIntegracao: nomeIntegracao || '', arquivos: arquivos || null }}
              setValues={onChange}
              submitText={null}
              onSubmit={null}
              inputPadding="8px 12px"
            />
            <Text style={{ color: "red", fontWeight: 600, fontSize: 14}}>APÓS CONFIGURAR O ARQUIVO, SALVE A SINCRONIZAÇÃO PARA EFETIVAR AS ALTERAÇÕES</Text>
          </>
        ) : (
          // ... existing code para outros ERPs ...
          formConfig.length > 0 && (
            <DynamicForm
              formConfig={formConfig}
              values={formValues}
              setValues={onChange}
              submitText={null}
              onSubmit={null}
              inputPadding="8px 12px"
            />
          )
        )}

        {/* Botão OAuth2 para Nibo */}
        {erp === 2 && type !== 2 && (
          <div style={{
            marginTop: 16,
            padding: 16,
            background: theme.background,
            borderRadius: 6
          }}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#262626',
                marginBottom: 8
              }}>
                Autorização OAuth2 - Nibo
              </Text>
              <Button
                type="primary"
                onClick={autorizarNibo}
                style={{
                  backgroundColor: theme.primary,
                  borderColor: theme.primary,
                  color: theme.white,
                  height: 40,
                  fontSize: 14,
                }}
              >
                {ExternalId ? 'Reautorizar com Nibo' : IsConfigured ? 'Autorizar Novamente' : 'Autorizar com Nibo'}
              </Button>
              <Text style={{
                display: 'block',
                fontSize: 12,
                color: '#8c8c8c',
                marginTop: 8
              }}>
                {ExternalId
                  ? 'Clique para renovar a autorização da conta Nibo (não altera dados existentes)'
                  : 'Clique para autorizar o acesso à sua conta Nibo via OAuth2'}
              </Text>
            </div>
          </div>
        )}

        {/* OAuth2 para Bling */}
        {erp === 6 && type !== 2 && (
          <div style={{
            marginTop: 16,
            padding: 16,
            background: theme.background,
            borderRadius: 6
          }}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#262626',
                marginBottom: 8
              }}>
                Autorização OAuth2 - Bling
              </Text>
              <Button
                type="primary"
                onClick={autorizarBling}
                loading={savingBeforeAuth}
                disabled={savingBeforeAuth}
                style={{
                  backgroundColor: '#f26522',
                  borderColor: '#f26522',
                  color: '#fff',
                  height: 40,
                  fontSize: 14,
                  cursor: savingBeforeAuth ? 'not-allowed' : 'pointer'
                }}
              >
                {blingAuthorizationPhase === 'saving'
                  ? 'Salvando...'
                  : blingAuthorizationPhase === 'authorizing'
                    ? 'Conectando ao Bling...'
                    : IsConfigured
                  ? 'Reautorizar com Bling'
                  : 'Autorizar com Bling'}
              </Button>
              <Text style={{
                display: 'block',
                fontSize: 12,
                color: '#8c8c8c',
                marginTop: 8
              }}>
                {IsConfigured
                  ? 'Clique para renovar a autorização da conta Bling (não altera dados já sincronizados)'
                  : 'Ao autorizar, a sincronização será salva automaticamente antes de abrir o Bling.'}
              </Text>
            </div>
          </div>
        )}

        {/* OAuth2 para Conta Azul */}
        {erp === 5 && type !== 2 && (
          <div style={{ 
            marginTop: 16, 
            padding: 16, 
            background: theme.background,
            borderRadius: 6 
          }}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ 
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#262626',
                marginBottom: 8
              }}>
                Autorização OAuth2 - Conta Azul
              </Text>
              <Button
                type="primary"
                onClick={autorizarContaAzul}
                loading={savingBeforeAuth}
                disabled={savingBeforeAuth}
                style={{
                  backgroundColor: theme.primary,
                  borderColor: theme.primary,
                  color: theme.white,
                  height: 40,
                  fontSize: 14,
                }}
              >
                {IsConfigured ? 'Reautorizar com Conta Azul' : 'Autorizar com Conta Azul'}
              </Button>
              <Text style={{
                display: 'block',
                fontSize: 12,
                color: '#8c8c8c',
                marginTop: 8
              }}>
                {IsConfigured
                  ? 'Clique para renovar a autorização da conta Conta Azul (não altera dados já sincronizados)'
                  : 'Clique para salvar a sincronização e autorizar o acesso à sua conta Conta Azul via OAuth2'}
              </Text>
              {!IsConfigured && (
                <Text style={{
                  display: 'block',
                  fontSize: 12,
                  color: 'red',
                  marginTop: 8,
                  fontWeight: 600
                }}>
                  UTILIZE E-MAIL E SENHA DO CONTA AZUL PRO PARA AUTORIZAR A SINCRONIZAÇÃO
                </Text>
              )}
            </div>
          </div>
        )}

      </Card>

      {showCompaniesModal && (
        <SelectNiboCompaniesModal
          open={showCompaniesModal}
          companies={niboCompanies}
          selected={selectedCompany}
          onChange={setSelectedCompany}
          onOk={() => {
            if (!selectedCompany) return;
            const company = niboCompanies.find(c => (c.id || c.value) === selectedCompany);
            const empresas = [{ id: selectedCompany, name: company?.name || company?.label || 'Empresa' }];
            const companyName = company?.label || company?.name || 'Empresa';
            setShowCompaniesModal(false);
            setNiboCompanies([]);
            setSelectedCompany(null);
            if (onNiboAuthorized) {
              onNiboAuthorized({ owner: niboOwner, empresas, companyName });
            }
            setNiboOwner(null);
          }}
          onCancel={() => {
            setShowCompaniesModal(false);
            setNiboCompanies([]);
            setSelectedCompany(null);
            setNiboOwner(null);
          }}
        />
      )}
    </>
  );
};

export default SynchronizationApiConfig;
